import React, { useCallback, useEffect, useState } from "react";
import { Button, Surface } from "@heroui/react";
import { LoadingState } from "../components/LoadingState";
import { StandaloneCatalogImport } from "../components/StandaloneCatalogImport";
import { PortalCheckbox } from "../components/form/PortalCheckbox";
import { PortalSelect } from "../components/form/PortalSelect";
import { PortalTextArea } from "../components/form/PortalTextArea";
import { PortalTextField } from "../components/form/PortalTextField";
import { useLocale } from "../context/LocaleContext";
import { apiFetch } from "../lib/apiClient";
import { navigatePortal } from "../lib/navigation";
import { showErrorToast, showSuccessToast } from "../lib/toast";

function replaceEmbedToken(value, token) {
  if (!value) return "";
  return value.replace(/YOUR_EMBED_TOKEN/g, token || "YOUR_EMBED_TOKEN");
}

export function AiLabelsPage() {
  const { t } = useLocale();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [products, setProducts] = useState([]);
  const [embedSnippet, setEmbedSnippet] = useState("");
  const [form, setForm] = useState({
    external_key: "",
    title: "",
    ai_media_status: "generated",
    image_urls: ""
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [s, sett, productPayload, setup, embed] = await Promise.all([
      apiFetch("/api/ai_labels/status").catch(() => null),
      apiFetch("/api/ai_labels/settings").catch(() => null),
      apiFetch("/api/ai_labels/products?limit=50").catch(() =>
        apiFetch("/api/portal/standalone_products?limit=50").catch(() => ({ products: [] }))
      ),
      apiFetch("/api/portal/setup_status").catch(() => null),
      apiFetch("/api/portal/embed_snippet").catch(() => null)
    ]);
    setStatus(s);
    setSettings(sett);
    setProducts(productPayload?.products || []);
    setEmbedSnippet(replaceEmbedToken(embed?.ai_labels_script_html, setup?.embed_token));
  }, []);

  useEffect(() => {
    let cancelled = false;
    load()
      .catch(() => {
        if (!cancelled) showErrorToast(t("aiLabels.loadError"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [load, t]);

  const enableModule = async () => {
    try {
      await apiFetch("/api/portal/modules", {
        method: "PATCH",
        body: JSON.stringify({ module_key: "ai_labels", enabled: true })
      });
      showSuccessToast(t("aiLabels.saved"));
      await load();
    } catch {
      showErrorToast(t("aiLabels.saveError"));
    }
  };

  const saveAppearance = async (patch) => {
    try {
      const next = await apiFetch("/api/ai_labels/settings", {
        method: "PATCH",
        body: JSON.stringify(patch)
      });
      setSettings(next);
      showSuccessToast(t("aiLabels.saved"));
    } catch {
      showErrorToast(t("aiLabels.saveError"));
    }
  };

  const createProduct = async () => {
    if (!form.external_key.trim()) {
      showErrorToast(t("aiLabels.productKeyRequired"));
      return;
    }
    setSaving(true);
    try {
      const urls = form.image_urls
        .split(/[\n,]+/)
        .map((u) => u.trim())
        .filter(Boolean);
      await apiFetch("/api/portal/standalone_products", {
        method: "POST",
        body: JSON.stringify({
          standalone_product: {
            external_key: form.external_key.trim(),
            title: form.title.trim() || form.external_key.trim(),
            ai_media_status: form.ai_media_status,
            image_urls: urls
          }
        })
      });
      setForm({ external_key: "", title: "", ai_media_status: "generated", image_urls: "" });
      showSuccessToast(t("aiLabels.saved"));
      await load();
    } catch (e) {
      showErrorToast(e?.message || t("aiLabels.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (product, statusValue) => {
    try {
      await apiFetch("/api/ai_labels/products", {
        method: "PATCH",
        body: JSON.stringify({
          product_gid: product.gid || `standalone://${product.external_key}`,
          status: statusValue
        })
      });
      await load();
    } catch {
      showErrorToast(t("aiLabels.saveError"));
    }
  };

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedSnippet);
      showSuccessToast(t("onboarding.snippetCopied"));
    } catch {
      showErrorToast(t("onboarding.copySnippet"));
    }
  };

  if (loading) return <LoadingState label={t("common.loading")} />;

  const subscribed = status?.subscribed ?? status?.enabled ?? false;
  const statusOptions = [
    { id: "none", label: t("aiLabels.statusNone") },
    { id: "generated", label: t("aiLabels.statusGenerated") },
    { id: "modified", label: t("aiLabels.statusModified") }
  ];

  return (
    <div className="portal-page-stack">
      <Surface className="portal-page-surface">
        <h2 className="portal-panel__title">{t("aiLabels.pageTitle")}</h2>

        {!subscribed ? (
          <div className="portal-banner portal-banner--info">
            <p className="font-medium">{t("aiLabels.billingRequiredHeading")}</p>
            <p className="mt-1">{t("aiLabels.billingRequiredBody")}</p>
            <div className="portal-panel__actions mt-3">
              <Button className="portal-accent-button" onPress={enableModule}>
                {t("aiLabels.enableStandalone")}
              </Button>
              <Button variant="secondary" onPress={() => navigatePortal("/portal/billing")}>
                {t("guarantees.goToBilling")}
              </Button>
            </div>
          </div>
        ) : null}

        <h3 className="portal-panel__section-title">{t("aiLabels.appearanceSection")}</h3>
        <p className="portal-panel__description">{t("aiLabels.appearanceHelp")}</p>

        <div className="grid gap-4 md:grid-cols-2">
          <PortalSelect
            label={t("aiLabels.positionLabel")}
            value={settings?.position || "bottom-left"}
            onChange={(value) => saveAppearance({ position: value })}
            options={[
              { id: "bottom-left", label: t("aiLabels.positionBottomLeft") },
              { id: "bottom-right", label: t("aiLabels.positionBottomRight") },
              { id: "top-left", label: t("aiLabels.positionTopLeft") },
              { id: "top-right", label: t("aiLabels.positionTopRight") }
            ]}
          />
          <PortalSelect
            label={t("aiLabels.contrastLabel")}
            value={settings?.contrast || "auto"}
            onChange={(value) => saveAppearance({ contrast: value })}
            options={[
              { id: "auto", label: t("aiLabels.contrastAuto") },
              { id: "black", label: t("aiLabels.contrastBlack") },
              { id: "white", label: t("aiLabels.contrastWhite") }
            ]}
          />
        </div>

        <div className="portal-checkbox-row">
          <PortalCheckbox
            isSelected={Boolean(settings?.enabled)}
            onChange={(checked) => saveAppearance({ enabled: checked, disclaimer_ack: true })}
          >
            {t("aiLabels.enabledToggle")}
          </PortalCheckbox>
        </div>
      </Surface>

      {embedSnippet ? (
        <Surface className="portal-page-surface">
          <h3 className="portal-panel__section-title">{t("aiLabels.embedSection")}</h3>
          <p className="portal-panel__description">{t("aiLabels.embedHelp")}</p>
          <div className="portal-banner portal-banner--info">
            <p className="font-medium m-0">{t("aiLabels.embedMatchHeading")}</p>
            <p className="mt-1 mb-0">{t("aiLabels.embedMatchBody")}</p>
          </div>
          <div className="portal-panel__code-wrap">
            <pre className="portal-panel__code">{embedSnippet}</pre>
            <Button variant="secondary" onPress={copyEmbed}>
              {t("onboarding.copySnippet")}
            </Button>
          </div>
        </Surface>
      ) : null}

      <StandaloneCatalogImport focus="ai" onImported={() => load()} />

      <Surface className="portal-page-surface">
        <h3 className="portal-panel__section-title">{t("aiLabels.labelingSection")}</h3>

        <div className="grid gap-4 md:grid-cols-2">
          <PortalTextField
            label={t("guarantees.colKey")}
            value={form.external_key}
            onChange={(v) => setForm((f) => ({ ...f, external_key: v }))}
          />
          <PortalTextField
            label={t("guarantees.colTitle")}
            value={form.title}
            onChange={(v) => setForm((f) => ({ ...f, title: v }))}
          />
          <PortalSelect
            label={t("aiLabels.aiMediaStatus")}
            value={form.ai_media_status}
            onChange={(value) => setForm((f) => ({ ...f, ai_media_status: value }))}
            options={statusOptions}
          />
          <div className="md:col-span-2">
            <PortalTextArea
              label={t("aiLabels.imageUrls")}
              value={form.image_urls}
              onChange={(v) => setForm((f) => ({ ...f, image_urls: v }))}
            />
          </div>
        </div>

        <div className="portal-panel__actions">
          <Button
            variant="primary"
            className="portal-accent-button"
            isDisabled={saving}
            onPress={createProduct}
          >
            {t("aiLabels.addProduct")}
          </Button>
        </div>

        {products.length === 0 ? (
          <p className="portal-panel__description">{t("aiLabels.noProducts")}</p>
        ) : (
          <div className="portal-data-grid__table-wrap overflow-auto">
            <table className="portal-data-grid__table w-full min-w-[560px] text-left text-sm">
              <thead>
                <tr>
                  <th className="py-2 pr-3">{t("guarantees.colTitle")}</th>
                  <th className="py-2 pr-3">{t("aiLabels.colStatus")}</th>
                  <th className="py-2">{t("aiLabels.colImages")}</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.gid || product.id}>
                    <td className="py-2 pr-3">
                      <div className="font-medium">{product.title || product.external_key}</div>
                      <div className="text-xs opacity-70">{product.external_key}</div>
                    </td>
                    <td className="py-2 pr-3">
                      <select
                        className="portal-inline-select"
                        aria-label={t("aiLabels.colStatus")}
                        value={product.ai_media_status || "none"}
                        onChange={(e) => updateStatus(product, e.target.value)}
                      >
                        {statusOptions.map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 text-xs opacity-70">
                      {(product.image_urls || []).length || (product.image_url ? 1 : 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Surface>

      <Surface className="portal-page-surface">
        <h3 className="portal-panel__section-title">{t("aiLabels.previewSection")}</h3>
        <div className="flex flex-wrap gap-4">
          <img
            src="/eu-ai-labels/generated/black.svg"
            alt={t("aiLabels.statusGenerated")}
            className="h-16 w-auto rounded-xl border border-[rgba(174,184,196,0.55)] bg-white p-2"
          />
          <img
            src="/eu-ai-labels/modified/black.svg"
            alt={t("aiLabels.statusModified")}
            className="h-16 w-auto rounded-xl border border-[rgba(174,184,196,0.55)] bg-white p-2"
          />
        </div>
      </Surface>
    </div>
  );
}
